import type { Department } from "../../../contexts/AuthProvider";
import { DailyPrintoutGroupingOption, type NurseFullRole } from "../../../utils/models";
import { getJobDisplayName, getRoleGroupsForRole } from "../../../utils/roles";
import { groupKeyFor } from "../group/groupKey";
import type { CustomSheetTab, ScheduleRow } from "../types";
import { mergeOverlappingShiftSheets, type SheetBucketMap } from "./timeOverlapMerge";

interface RowMatchesCustomTabArgs {
	row: ScheduleRow;
	tab: CustomSheetTab;
	department: Department;
}

/**
 * Does this row belong on this custom tab?
 *
 * - `selectionMode === "jobs"` → match by `row.nurse?.jobId`. Rows without a `jobId` never match.
 * - `selectionMode === "role_groups"` → resolve the row's role-group memberships via the
 *   department's `role_groups` map, and match if any membership is in `tab.selectedKeys`.
 *
 * An empty `selectedKeys` never matches. A missing `department.role_groups` blocks all
 * role-group-mode matches.
 */
export const rowMatchesCustomTab = ({ row, tab, department }: RowMatchesCustomTabArgs): boolean => {
	if (tab.selectedKeys.length === 0) return false;
	if (tab.selectionMode === "jobs") {
		const jobId = row.nurse?.jobId;
		return jobId ? tab.selectedKeys.includes(jobId) : false;
	}
	if (!department.role_groups) return false;
	const role = row.role as NurseFullRole | undefined;
	if (!role) return false;
	const rowGroups = getRoleGroupsForRole(role, department);
	return rowGroups.some((g) => tab.selectedKeys.includes(g));
};

/**
 * Default display name for a custom tab when the user hasn't set one (mapping §7.3).
 *
 * Empty selections → `"Untitled"`. Otherwise:
 * - `"jobs"` mode → joined job display names (via `getJobDisplayName`), falling back to the raw
 *   id for unknown jobs
 * - `"role_groups"` mode → joined role-group labels (already human-readable)
 */
export const defaultCustomTabName = (tab: CustomSheetTab, department: Department): string => {
	if (tab.selectedKeys.length === 0) return "Untitled";
	if (tab.selectionMode === "jobs") {
		return tab.selectedKeys.map((jobId) => getJobDisplayName(jobId, department) || jobId).join(" / ");
	}
	return tab.selectedKeys.join(" / ");
};

interface SplitCustomEntriesArgs {
	tabs: readonly CustomSheetTab[];
	sortedEntries: ReadonlyArray<readonly [string, ScheduleRow[]]>;
	department: Department;
	jobManagementEnabled: boolean;
}

/**
 * A single candidate sheet from the custom-tabs flow (mapping §7.3).
 *
 * `sheetKey` is the raw display name (before dedup + Excel sanitization). `entries` is the list
 * of section-grouping entries that landed on this sheet.
 */
export interface CustomTabCandidate {
	sheetKey: string;
	entries: Array<[string, ScheduleRow[]]>;
}

/**
 * Build the custom-tab sheet candidates from the user's tab configuration (mapping §7.3).
 *
 * Rules:
 * - **First-tab-wins**: a row that matches multiple tabs is claimed by the first tab in the list.
 * - **`breakByShift`**: subdivide the tab's sections by exact shift window, then fold overlapping
 *   windows together via §6.3-style proximity merging. Each resulting shift window becomes its
 *   own sheet, named `"<tab display name> <shift label>"`.
 * - **Unmatched rows**: rows not claimed by any tab collect into an `"Other"` candidate so
 *   nothing is silently dropped.
 *
 * Returns candidates **before** dedup / sanitization (those run in the next step).
 */
export const buildCustomTabCandidates = (args: SplitCustomEntriesArgs): CustomTabCandidate[] => {
	const { tabs, sortedEntries, department, jobManagementEnabled } = args;
	const matchedRows = new Set<ScheduleRow>();
	const candidates: CustomTabCandidate[] = [];

	for (const tab of tabs) {
		const tabEntries: Array<[string, ScheduleRow[]]> = [];
		for (const [groupingKey, rows] of sortedEntries) {
			const matched = rows.filter((row) => {
				if (matchedRows.has(row)) return false;
				if (rowMatchesCustomTab({ row, tab, department })) {
					matchedRows.add(row);
					return true;
				}
				return false;
			});
			if (matched.length > 0) tabEntries.push([groupingKey, matched]);
		}

		if (tabEntries.length === 0) continue;

		const tabName = tab.name?.trim() || defaultCustomTabName(tab, department);

		if (tab.breakByShift) {
			const shiftSubMap: SheetBucketMap = new Map();
			for (const [groupingKey, rows] of tabEntries) {
				const rowsByShift = new Map<string, ScheduleRow[]>();
				for (const row of rows) {
					const shiftKey = groupKeyFor({ row, groupingOption: DailyPrintoutGroupingOption.START_TIME, jobManagementEnabled });
					const existing = rowsByShift.get(shiftKey);
					if (existing) existing.push(row);
					else rowsByShift.set(shiftKey, [row]);
				}
				for (const [shiftKey, sheetRows] of rowsByShift) {
					const existing = shiftSubMap.get(shiftKey);
					if (existing) existing.push([groupingKey, sheetRows]);
					else shiftSubMap.set(shiftKey, [[groupingKey, sheetRows]]);
				}
			}
			const mergedShifts = mergeOverlappingShiftSheets(shiftSubMap);
			for (const [shiftKey, entries] of mergedShifts) {
				candidates.push({ sheetKey: `${tabName} ${shiftKey}`, entries });
			}
		} else {
			candidates.push({ sheetKey: tabName, entries: tabEntries });
		}
	}

	// Unmatched rows go into the "Other" tab so nothing is silently dropped (mapping §7.3).
	const unmatchedEntries: Array<[string, ScheduleRow[]]> = [];
	for (const [groupingKey, rows] of sortedEntries) {
		const unmatched = rows.filter((row) => !matchedRows.has(row));
		if (unmatched.length > 0) unmatchedEntries.push([groupingKey, unmatched]);
	}
	if (unmatchedEntries.length > 0) {
		candidates.push({ sheetKey: "Other", entries: unmatchedEntries });
	}

	return candidates;
};
